/**
 * Lesson display metadata - type label and duration
 * Handles text lessons, video lessons, and quizzes consistently across sidebars and meta
 */

import { getDuration } from './duration';

export interface LessonMetaInput {
  text_lesson?: string | Record<string, unknown>;
  video_lesson?: { duration?: number }[];
  time_to_read?: string;
  is_quiz?: boolean;
  isQuiz?: boolean;
  quiz?: { question_count?: number; assessment?: unknown[] };
}

/** True when lesson has meaningful text content (string or RTE object) */
function hasTextContent(textLesson: string | Record<string, unknown> | undefined): boolean {
  if (!textLesson) return false;
  if (typeof textLesson === 'string') return textLesson.trim().length > 0;
  if (typeof textLesson === 'object') return Object.keys(textLesson).length > 0;
  return false;
}

/** True when lesson has video content (video_lesson with duration or sources) */
function hasVideoContent(lesson: LessonMetaInput): boolean {
  const vl = lesson?.video_lesson;
  if (!vl || !Array.isArray(vl)) return false;
  const first = vl[0];
  if (!first) return false;
  return (first.duration != null && first.duration > 0) || !!(first as { sources?: unknown[] }).sources?.length;
}

/** True when lesson is a quiz: CMS is_quiz flag (trusted) or has actual quiz content. Ignores API isQuiz alone to avoid merge overwrites. */
function isQuizLesson(lesson: LessonMetaInput): boolean {
  if (lesson?.is_quiz === true) return true;
  const q = lesson?.quiz;
  if (!q) return false;
  return (q.question_count ?? 0) > 0 || (q.assessment?.length ?? 0) > 0;
}

/** Get lesson type label: Text Lesson, Video Lesson, or Quiz. Prioritizes content over flags. */
export function getLessonTypeLabel(lesson: LessonMetaInput): string {
  if (hasVideoContent(lesson)) return 'Video Lesson';
  if (hasTextContent(lesson?.text_lesson)) return 'Text Lesson';
  if (isQuizLesson(lesson)) return 'Quiz';
  return 'Video Lesson';
}

/** Get hover action label: Start/Resume/Restart + quiz or lesson. Uses same content-first logic as getLessonTypeLabel. */
export function getLessonActionLabel(
  lesson: LessonMetaInput,
  status: 'not_started' | 'in_progress' | 'completed'
): string {
  const typeLabel = getLessonTypeLabel(lesson);
  const noun = typeLabel === 'Quiz' ? 'quiz' : 'lesson';
  switch (status) {
    case 'completed':
      return `Restart ${noun}`;
    case 'in_progress':
      return `Resume ${noun}`;
    default:
      return `Start ${noun}`;
  }
}

/** Get lesson duration string - uses same content-first order as getLessonTypeLabel */
export function getLessonDurationDisplay(lesson: LessonMetaInput): string {
  if (hasVideoContent(lesson) && lesson?.video_lesson?.[0]?.duration != null) {
    const formatted = getDuration(lesson.video_lesson[0].duration);
    if (formatted) return formatted;
  }
  if (hasTextContent(lesson?.text_lesson) && lesson?.time_to_read) {
    return lesson.time_to_read;
  }
  if (isQuizLesson(lesson) && lesson?.quiz?.question_count != null) {
    const count = lesson.quiz.question_count;
    return count === 1 ? '1 Question' : `${count} Questions`;
  }
  if (lesson?.video_lesson?.[0]?.duration != null) {
    const formatted = getDuration(lesson.video_lesson[0].duration);
    if (formatted) return formatted;
  }
  if (lesson?.time_to_read) return lesson.time_to_read;
  return '';
}
